import React, { useState, useEffect } from 'react';
import { getContactMessages, updateMessageStatus } from '../../utils/adminApi';
import { Mail, Filter, Reply, Send, X, User, Phone, Calendar, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Textarea } from '../ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BUSINESS_EMAIL = 'orders@primedigital.es';

const MessagesManagement = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    loadMessages();
  }, [statusFilter]);

  const loadMessages = async () => {
    try {
      const data = await getContactMessages(statusFilter);
      setMessages(data.messages);
    } catch (error) {
      console.error('Error loading messages:', error);
      toast.error('Error al cargar mensajes');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (messageId, newStatus) => {
    try {
      await updateMessageStatus(messageId, newStatus);
      toast.success('Estado actualizado');
      loadMessages();
    } catch (error) {
      console.error('Error updating message:', error);
      toast.error('Error al actualizar estado');
    }
  };

  const openMessage = async (message) => {
    setSelectedMessage(message);
    // Mark as read if new
    if (message.status === 'new') {
      await handleStatusChange(message.message_id, 'read');
    }
  };

  const openReplyModal = (message) => {
    setSelectedMessage(message);
    setReplyText(`Hola ${message.name},\n\nGracias por contactar con PrimeDigital.\n\n\n\nSaludos,\nEquipo PrimeDigital`);
    setShowReplyModal(true);
  };

  const sendReply = async () => {
    if (!replyText.trim()) {
      toast.error('Por favor escribe un mensaje');
      return;
    }

    setSending(true);
    try {
      // Open email client with pre-filled data
      const subject = encodeURIComponent(`Re: Mensaje de ${selectedMessage.name} - PrimeDigital`);
      const body = encodeURIComponent(replyText);
      const mailtoLink = `mailto:${selectedMessage.email}?subject=${subject}&body=${body}&cc=${BUSINESS_EMAIL}`;
      
      window.open(mailtoLink, '_blank');
      
      // Update status to responded
      await handleStatusChange(selectedMessage.message_id, 'responded');
      
      setShowReplyModal(false);
      setReplyText('');
      toast.success('Email abierto - marca como respondido');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al enviar respuesta');
    } finally {
      setSending(false);
    }
  };

  const copyEmail = (email) => {
    navigator.clipboard.writeText(email);
    toast.success('Email copiado');
  };

  if (loading) return <div className="text-[#D4AF37]">Cargando mensajes...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center space-x-3">
            <Mail className="w-8 h-8 text-[#D4AF37]" />
            <span>Mensajes de Contacto</span>
          </h1>
          <p className="text-gray-400 mt-1">Las respuestas se envían a {BUSINESS_EMAIL}</p>
        </div>
        <div className="flex items-center space-x-2">
          <Filter className="w-5 h-5 text-gray-400" />
          <select
            value={statusFilter || ''}
            onChange={(e) => setStatusFilter(e.target.value || null)}
            className="px-4 py-2 bg-[#1a1d23] border border-[#2a3441] text-white rounded-lg focus:border-[#D4AF37]"
          >
            <option value="">Todos</option>
            <option value="new">Nuevos</option>
            <option value="read">Leídos</option>
            <option value="responded">Respondidos</option>
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="p-4 bg-gradient-to-br from-[#1a1d23] to-[#141619] rounded-xl border border-[#2a3441]">
          <p className="text-sm text-gray-400">Total</p>
          <p className="text-2xl font-bold text-white">{messages.length}</p>
        </div>
        <div className="p-4 bg-gradient-to-br from-yellow-500/10 to-[#141619] rounded-xl border border-yellow-500/30">
          <p className="text-sm text-yellow-500">Nuevos</p>
          <p className="text-2xl font-bold text-yellow-500">{messages.filter(m => m.status === 'new').length}</p>
        </div>
        <div className="p-4 bg-gradient-to-br from-blue-500/10 to-[#141619] rounded-xl border border-blue-500/30">
          <p className="text-sm text-blue-500">Leídos</p>
          <p className="text-2xl font-bold text-blue-500">{messages.filter(m => m.status === 'read').length}</p>
        </div>
        <div className="p-4 bg-gradient-to-br from-green-500/10 to-[#141619] rounded-xl border border-green-500/30">
          <p className="text-sm text-green-500">Respondidos</p>
          <p className="text-2xl font-bold text-green-500">{messages.filter(m => m.status === 'responded').length}</p>
        </div>
      </div>

      <div className="space-y-4">
        {messages.map((message) => (
          <div 
            key={message.message_id} 
            className={`p-6 bg-gradient-to-br from-[#1a1d23] to-[#141619] rounded-2xl border transition-all duration-300 cursor-pointer hover:border-[#D4AF37]/50 ${
              message.status === 'new' ? 'border-yellow-500/50' : 'border-[#2a3441]'
            }`}
            onClick={() => openMessage(message)}
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-full ${message.status === 'new' ? 'bg-yellow-500/20' : 'bg-[#2a3441]'}`}>
                  <User className={`w-5 h-5 ${message.status === 'new' ? 'text-yellow-500' : 'text-[#D4AF37]'}`} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">{message.name}</h3>
                  <button 
                    onClick={(e) => { e.stopPropagation(); copyEmail(message.email); }}
                    className="text-sm text-gray-400 hover:text-[#D4AF37] transition-colors flex items-center space-x-1"
                  >
                    <span>{message.email}</span>
                    <ExternalLink className="w-3 h-3" />
                  </button>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  message.status === 'new' ? 'bg-yellow-500/10 text-yellow-500 border border-yellow-500/30' :
                  message.status === 'read' ? 'bg-blue-500/10 text-blue-500 border border-blue-500/30' :
                  'bg-green-500/10 text-green-500 border border-green-500/30'
                }`}>
                  {message.status === 'new' ? 'Nuevo' : message.status === 'read' ? 'Leído' : 'Respondido'}
                </span>
                <button
                  onClick={(e) => { e.stopPropagation(); openReplyModal(message); }}
                  className="p-2 bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 rounded-lg transition-colors"
                  title="Responder"
                >
                  <Reply className="w-4 h-4 text-[#D4AF37]" />
                </button>
              </div>
            </div>
            
            {message.phone && (
              <div className="flex items-center space-x-2 text-sm text-gray-400 mb-3">
                <Phone className="w-4 h-4" />
                <span>{message.phone}</span>
              </div>
            )}
            
            <p className="text-gray-300 line-clamp-2">{message.message}</p>
            
            <div className="flex items-center space-x-2 text-xs text-gray-500 mt-3">
              <Calendar className="w-3 h-3" />
              <span>{new Date(message.created_at).toLocaleString('es-ES')}</span>
            </div>
          </div>
        ))}
        
        {messages.length === 0 && (
          <div className="text-center py-12">
            <Mail className="w-12 h-12 text-[#3a3d42] mx-auto mb-4" />
            <p className="text-gray-400">No hay mensajes</p>
          </div>
        )}
      </div>

      {/* Reply Modal */}
      <Dialog open={showReplyModal} onOpenChange={setShowReplyModal}>
        <DialogContent className="bg-[#1a1d23] border-[#2a3441] text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#D4AF37] flex items-center space-x-2">
              <Reply className="w-5 h-5" />
              <span>Responder a {selectedMessage?.name}</span>
            </DialogTitle>
          </DialogHeader>
          
          {selectedMessage && (
            <div className="space-y-4">
              {/* Original Message */}
              <div className="p-4 bg-[#0a0c0f] rounded-lg border border-[#2a3441]">
                <p className="text-xs text-gray-500 mb-2">Mensaje original:</p>
                <p className="text-gray-300 text-sm">{selectedMessage.message}</p>
              </div>
              
              {/* Reply To */}
              <div className="flex items-center space-x-2 text-sm">
                <span className="text-gray-400">Para:</span>
                <span className="text-white">{selectedMessage.email}</span>
                <span className="text-gray-500">|</span>
                <span className="text-gray-400">CC:</span>
                <span className="text-[#D4AF37]">{BUSINESS_EMAIL}</span>
              </div>
              
              {/* Reply Text */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">Tu respuesta:</label>
                <Textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  className="w-full h-48 bg-[#0a0c0f] border-[#2a3441] text-white focus:border-[#D4AF37]"
                  placeholder="Escribe tu respuesta..."
                />
              </div>
              
              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-[#2a3441]">
                <button
                  onClick={() => setShowReplyModal(false)}
                  className="px-4 py-2 bg-[#2a3441] text-gray-300 rounded-lg hover:bg-[#3a4451] transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={sendReply}
                  disabled={sending}
                  className="px-6 py-2 bg-gradient-to-r from-[#D4AF37] to-[#F4C542] text-[#141619] rounded-lg font-medium hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all flex items-center space-x-2 disabled:opacity-50"
                >
                  <Send className="w-4 h-4" />
                  <span>{sending ? 'Enviando...' : 'Abrir Email'}</span>
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MessagesManagement;
